import * as PIXI from "pixi.js";
import anime from "animejs";
import { PixiSettings } from "../../../../../../pixiSettings";
import {
  ASPECT_RATIO_BREAKPOINTS,
  ULTRA_WIDE,
} from "../../../../../../Contexts/DimensionsContext";
import { FONT_STYLES, scaleStyle } from "../../../../../../fontStyles";
import { FEATURES_CONFIG } from "../../../../../../featuresConfig";
import { getNewUITexture } from "../../../../../../assets";
import { soundManager } from "../../../../../../SoundManager";
import { SOUND_NAME } from "../../../../../../sounds";

// ─── Max win multiplier ───────────────────────────────────────────────────────
const MAX_WIN_MULTIPLIER =
  FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER ||
  FEATURES_CONFIG.HOLD_AND_WIN?.MAX_WIN_MULTIPLIER ||
  10000;

// ─── Slider constants (mirrors PaytableSlider) ─────────────────────────────────
const NAV_THROTTLE_MS = 100;
const SWIPE_THRESHOLD = 50;
const VELOCITY_THRESHOLD = 0.3;
const AUTOPLAY_INTERVAL_MS = 3000;
// Below this pointer travel, a press+release counts as a tap (closes the popup),
// not a swipe. Distinguishes tap-to-start from a deliberate drag.
const TAP_MOVE_THRESHOLD = 10;
// Pagination dots use real spritesheet circles (not Graphics) — PIXI Graphics
// circles render with jagged / aliased edges that a border-radius fix can't solve.
const DOT_TEX_NAME_ACTIVE = "button_buy_bonus_background"; // yellow circle
const DOT_TEX_NAME_INACTIVE = "volume_bar_handle"; // white circle

// ============================================================================
// INTRO POPUP LAYOUT CONFIGURATION
// Fine-tune background scale and padding per device and orientation.
// bgScaleMult  : how large the background oval fills the content area (1.0 = exact fit)
// padMult      : top/bottom protected zone = uiSymbolSize × padMult
// sidePadMult  : left/right protected zone = uiSymbolSize × sidePadMult
// ============================================================================

const LAYOUT_MOBILE_SMALL_PORTRAIT = {
  bgScaleMult: 1.0,
  padMult: 0.5,
  sidePadMult: 0.1,
};

const LAYOUT_MOBILE_SMALL_LANDSCAPE = {
  bgScaleMult: 1.0,
  padMult: 0.75,
  sidePadMult: 0.5,
};

const LAYOUT_MOBILE_PORTRAIT = {
  bgScaleMult: 1.05,
  padMult: 0.5,
  sidePadMult: 0.1,
};

const LAYOUT_MOBILE_LANDSCAPE = {
  bgScaleMult: 1.0,
  padMult: 0.75,
  sidePadMult: 0.5,
};

const LAYOUT_TABLET_PORTRAIT = {
  bgScaleMult: 1.05,
  padMult: 1.0,
  sidePadMult: 0.2,
};

const LAYOUT_TABLET_LANDSCAPE = {
  bgScaleMult: 0.9,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_SMALL_DESKTOP = {
  bgScaleMult: 1.2,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_DESKTOP = {
  bgScaleMult: 1.25,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_WIDE_DESKTOP = {
  bgScaleMult: 1.1,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_ULTRA_WIDE_DESKTOP = {
  bgScaleMult: 0.975,
  padMult: 1.0,
  sidePadMult: 0.5,
};

const LAYOUT_CONFIGS = {
  MOBILE_SMALL_PORTRAIT: LAYOUT_MOBILE_SMALL_PORTRAIT,
  MOBILE_SMALL_LANDSCAPE: LAYOUT_MOBILE_SMALL_LANDSCAPE,
  MOBILE_PORTRAIT: LAYOUT_MOBILE_PORTRAIT,
  MOBILE_LANDSCAPE: LAYOUT_MOBILE_LANDSCAPE,
  TABLET_PORTRAIT: LAYOUT_TABLET_PORTRAIT,
  TABLET_LANDSCAPE: LAYOUT_TABLET_LANDSCAPE,
  SMALL_DESKTOP: LAYOUT_SMALL_DESKTOP,
  DESKTOP: LAYOUT_DESKTOP,
  WIDE_DESKTOP: LAYOUT_WIDE_DESKTOP,
  ULTRA_WIDE_DESKTOP: LAYOUT_ULTRA_WIDE_DESKTOP,
};

const getLayoutConfig = (device, isPortrait, isMobileSmall, screenWidth) => {
  const orientation = isPortrait ? "PORTRAIT" : "LANDSCAPE";
  if (isMobileSmall) return LAYOUT_CONFIGS[`MOBILE_SMALL_${orientation}`];
  if (device === PixiSettings.DEVICE.MOBILE)
    return LAYOUT_CONFIGS[`MOBILE_${orientation}`];
  if (device === PixiSettings.DEVICE.TABLET)
    return LAYOUT_CONFIGS[`TABLET_${orientation}`];
  if (screenWidth && screenWidth >= ULTRA_WIDE.BREAKPOINT)
    return LAYOUT_CONFIGS.ULTRA_WIDE_DESKTOP;
  if (
    screenWidth &&
    screenWidth >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH
  )
    return LAYOUT_CONFIGS.WIDE_DESKTOP;
  if (
    screenWidth &&
    screenWidth <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH
  )
    return LAYOUT_CONFIGS.SMALL_DESKTOP;
  return LAYOUT_CONFIGS.DESKTOP;
};
const SLIDE_CONTENT_SCALE = 0.7;

// ---------------------------------------------------------------------------
// PORTRAIT / LANDSCAPE element ratios — slider content between max-win & tap
// Fixed slot positions (ratios of slide area) keep every slide aligned identically.
// ---------------------------------------------------------------------------
const PORTRAIT_CFG = {
  logoWRatio: 0.45,
  logoYRatio: -0.44,
  maxWinFontRatio: 0.06,
  maxWinYRatio: -0.33,
  slideTopGapRatio: 0.03,
  dotsGapRatio: 0.13,
  dotSizeRatio: 0.02,
  dotGapRatio: 0.016,
  tapFontRatio: 0.035,
  tapGapRatio: 0.04,
  tapYRatio: 0.38,
  // Slide area bottom — independent of dots (bgCY + bgH × ratio)
  slideAreaBottomRatio: 0.22,
  slideTopCenterRatio: 0.14,
  slideBottomCenterRatio: 0.35,
  slideImageOffsetYRatio: 0.085,
  slideDescTopRatio: 0.78,
  slideTopMaxWRatio: 0.5,
  slideBottomMaxWRatio: 0.56,
  slideTopMaxHRatio: 0.2,
  slideBottomMaxHRatio: 0.18,
  slideSingleCenterRatio: 0.36,
  slideSingleMaxWRatio: 0.6,
  slideSingleMaxHRatio: 0.44,
};

const LANDSCAPE_CFG = {
  logoWRatio: 0.24,
  logoYRatio: -0.5,
  maxWinFontRatio: 0.036,
  maxWinYRatio: -0.329,
  slideTopGapRatio: 0,
  dotsGapRatio: 0.055,
  dotSizeRatio: 0.014,
  dotGapRatio: 0.01,
  tapFontRatio: 0.0225,
  tapGapRatio: 0.13,
  tapYOffsetRatio: 0.145,
  contentCenterYRatio: 0.08,
  cardWRatio: 0.29,
  slideAreaBottomPadRatio: 0.012,
  slidePairedRowCenterRatio: 0.34,
  slidePairedRowMaxHRatio: 0.52,
  slidePairedLeftMaxWRatio: 0.36,
  slidePairedRightMaxWRatio: 0.36,
  slidePairedGapRatio: 0.03,
  slideImageOffsetYRatio: 0,
  slideDescTopRatio: 0.75,
  slideSingleCenterRatio: 0.38,
  slideSingleMaxWRatio: 0.44,
  slideSingleMaxHRatio: 0.4,
};

// Slide definitions — top + optional bottom image, description below.
// Shared assets for portrait and landscape.
const SLIDE_DEFS = [
  {
    topTex: "intro_feature_collect_pot",
    bottomTex: "intro_feature_collect_coins",
    descKey: "start_page_collect_title",
    descDefault: "Collects values",
    portraitSizeMult: 2.8,
    landscapeSizeMult: 1.75,
    landscapeCenterOffsetRatio: 0,
  },
  {
    topTex: "intro_feature_mystery_pot",
    bottomTex: "intro_feature_mystery_coins",
    descKey: "start_page_mystery_title",
    descDefault: "Turns into Jackpots",
    portraitSizeMult: 2.8,
    landscapeSizeMult: 1.75,
    landscapeCenterOffsetRatio: 0,
  },
  {
    topTex: "intro_feature_double_pot",
    bottomTex: "intro_feature_double_coins",
    descKey: "start_page_double_title",
    descDefault: "2x all values",
    portraitSizeMult: 2.8,
    landscapeSizeMult: 1.75,
    landscapeCenterOffsetRatio: 0,
  },
  {
    topTex: "intro_feature_superturbo",
    bottomTex: null,
    descKey: "start_page_turbo_title",
    descDefault: "Faster gameplay with Super Turbo spins",
    portraitSizeMult: 1.65,
    landscapeSizeMult: 2.75,
    landscapeCenterOffsetRatio: -0.05,
  },
];

const TOTAL_SLIDES = SLIDE_DEFS.length;

/**
 * IntroPopupComposedController — PIXI display objects for the composed intro
 * popup: static chrome (bg, logo, max win, tap-to-start) plus a swipeable
 * feature-image slider with pagination dots and swipe navigation.
 */
class IntroPopupComposedController {
  constructor(container, dimensionsContext, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.t = t;

    // Static display objects
    this._blackBg = null;
    this._stormBg = null;
    this._logo = null;
    this._maxWinText = null;
    this._tapContainer = null;
    this._tapText = null;

    // Slider display objects
    this._sliderGroup = null;
    this._slidesMaskContainer = null;
    this._slidesMaskGfx = null;
    this._slidesTrack = null;
    this._realSlides = [];
    this._dotsContainer = null;
    this._dotEntries = [];

    // Textures (cached at init — assets already loaded)
    this._bgPortraitTex = PIXI.Assets.get("intro_background_portrait");
    this._bgLandscapeTex = PIXI.Assets.get("intro_background_landscape");
    this._logoTex = PIXI.Assets.get("logo");

    // Click handler (set by component via setClickHandler)
    this._clickHandler = null;
    // Fullscreen handler (set via setFullscreenHandler) — triggered on swipe so
    // touch navigation enters fullscreen just like a tap does, without closing.
    this._fullscreenHandler = null;

    // Hover / press animation refs (for cleanup)
    this._hoverAnim = null;
    this._pressAnim = null;
    this._isPressAnimating = false;

    // Intro entrance animation refs
    this._introAnims = null;

    // Slider state
    this._currentSlide = 0;
    this._slideWidth = 0;
    this._slideHeight = 0;
    this._trackPos = { x: 0 };
    this._trackAnim = null;
    this._isTransitioning = false;
    this._isJumping = false;
    this._isPaginationNav = false;
    this._lastNavTime = 0;
    this._fadeEasing = anime.easing("easeInOutCubic");

    // Autoplay state
    this._autoplayTickerCallback = null;
    this._autoplayElapsed = 0;

    // Drag state
    this._isDragging = false;
    this._dragStartX = 0;
    this._dragStartTrackX = 0;
    this._dragCurrentX = 0;
    this._dragStartTime = 0;

    // Layout cache
    this._dotsCenterY = 0;
    this._tapTextY = 0;
    this._layoutBgW = 0;
    this._layoutCfg = PORTRAIT_CFG;
    this._layoutBgCX = 0;
    this._layoutBgCY = 0;
    this._slideLayout = null;

    this.initialized = false;
    this._initialize();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  _initialize() {
    this.container.sortableChildren = true;

    this._createBlackBackground();
    this._createStormBackground();
    this._createLogo();
    this._createMaxWinText();
    this._createSlider();
    this._createTapText();

    this.initialized = true;
    this._updateLayout();
  }

  _createBlackBackground() {
    this._blackBg = new PIXI.Sprite(PIXI.Texture.WHITE);
    this._blackBg.tint = 0x000000;
    this._blackBg.zIndex = 0;
    this._blackBg.eventMode = "static";
    // Default (arrow) cursor — the background itself isn't a button, only the
    // "tap to continue" text and pagination dots show a hand cursor.
    this._blackBg.cursor = "default";
    // Full-screen swipe surface. Drag handlers are bound in _bindDragHandlers;
    // a clean tap (no drag) closes the popup via the swipe/tap split in
    // _onDragEnd. Pagination dots and the sound button sit on top as separate
    // interactive objects, so they intercept their own taps before the backdrop.
    this.container.addChild(this._blackBg);
  }

  _createStormBackground() {
    const isPortrait =
      this.dimensionsContext.orientation === PixiSettings.ORIENTATION.PORTRAIT;
    const tex = isPortrait ? this._bgPortraitTex : this._bgLandscapeTex;
    if (!tex) return;

    this._stormBg = new PIXI.Sprite(tex);
    this._stormBg.anchor.set(0.5);
    this._stormBg.zIndex = 0.5;
    this._stormBg.eventMode = "none";
    this.container.addChild(this._stormBg);
  }

  _createLogo() {
    if (!this._logoTex) return;

    this._logo = new PIXI.Sprite(this._logoTex);
    this._logo.anchor.set(0.5);
    this._logo.zIndex = 1;
    this._logo.eventMode = "none";
    this.container.addChild(this._logo);
  }

  _createMaxWinText() {
    const text = `MAX WIN ${MAX_WIN_MULTIPLIER.toLocaleString("en-US")}X`;
    const style = new PIXI.TextStyle(
      scaleStyle(FONT_STYLES.winText, 24, { align: "center" }),
    );

    this._maxWinText = new PIXI.Text(text, style);
    this._maxWinText.anchor.set(0.5);
    this._maxWinText.resolution = 2;
    this._maxWinText.zIndex = 1;
    this._maxWinText.eventMode = "none";
    this.container.addChild(this._maxWinText);
  }

  _buildSlideContainer(def, name) {
    const container = new PIXI.Container();
    container.name = name;
    container.slideDef = def;

    const topTex = PIXI.Assets.get(def.topTex) || PIXI.Texture.EMPTY;
    const topSprite = new PIXI.Sprite(topTex);
    topSprite.anchor.set(0.5, 0.5);
    topSprite.eventMode = "none";
    container.addChild(topSprite);

    let bottomSprite = null;
    if (def.bottomTex) {
      const bottomTex = PIXI.Assets.get(def.bottomTex) || PIXI.Texture.EMPTY;
      bottomSprite = new PIXI.Sprite(bottomTex);
      bottomSprite.anchor.set(0.5, 0.5);
      bottomSprite.eventMode = "none";
      container.addChild(bottomSprite);
    }

    const descStr = this.t(def.descKey, { defaultValue: def.descDefault });
    const descText = new PIXI.Text(
      descStr,
      new PIXI.TextStyle({
        fontSize: 14,
        fill: 0xffffff,
        align: "center",
      }),
    );
    descText.anchor.set(0.5, 0);
    descText.resolution = 2;
    descText.eventMode = "none";
    container.addChild(descText);

    return { container, topSprite, bottomSprite, descText, def };
  }

  _createSlider() {
    this._sliderGroup = new PIXI.Container();
    this._sliderGroup.name = "slider_group";
    this._sliderGroup.zIndex = 1;
    this._sliderGroup.alpha = 0;
    this._sliderGroup.scale.set(0, 0);
    this.container.addChild(this._sliderGroup);

    this._slidesMaskContainer = new PIXI.Container();
    this._slidesMaskContainer.name = "slides_mask_container";
    // Non-interactive: the full-screen backdrop (_blackBg) is the swipe surface,
    // so this must not shadow its hit area. Masking still works regardless.
    this._slidesMaskContainer.eventMode = "none";
    this._sliderGroup.addChild(this._slidesMaskContainer);

    this._slidesMaskGfx = new PIXI.Graphics();
    this._slidesMaskContainer.addChild(this._slidesMaskGfx);
    this._slidesMaskContainer.mask = this._slidesMaskGfx;

    this._slidesTrack = new PIXI.Container();
    this._slidesTrack.name = "slides_track";
    this._slidesMaskContainer.addChild(this._slidesTrack);

    this._realSlides = SLIDE_DEFS.map((def, index) => {
      return this._buildSlideContainer(def, `intro_slide_${index}`);
    });

    this._rebuildTrackSlides();
    this._bindDragHandlers();
    this._createPaginationDots();
  }

  _rebuildTrackSlides() {
    if (!this._slidesTrack) return;

    this._slidesTrack.removeChildren();

    const cloneLast = this._buildSlideContainer(
      SLIDE_DEFS[TOTAL_SLIDES - 1],
      `intro_slide_clone_last`,
    );
    this._slidesTrack.addChild(cloneLast.container);

    for (const slide of this._realSlides) {
      this._slidesTrack.addChild(slide.container);
    }

    const cloneFirst = this._buildSlideContainer(
      SLIDE_DEFS[0],
      `intro_slide_clone_first`,
    );
    this._slidesTrack.addChild(cloneFirst.container);

    this._layoutTrackSlidePositions();
    this._syncTrackPosition(true);
  }

  _layoutTrackSlidePositions() {
    if (!this._slidesTrack || !this._slideWidth) return;

    for (let i = 0; i < this._slidesTrack.children.length; i++) {
      const child = this._slidesTrack.children[i];
      child.x = i * this._slideWidth;
      child.y = 0;
      this._layoutSlideContents(child);
    }
  }

  _computeSlideLayout(sw, sh, cfg) {
    const scale = SLIDE_CONTENT_SCALE;
    const imageOffsetY = sh * (cfg.slideImageOffsetYRatio ?? 0);
    const layout = {
      cx: sw / 2,
      topCenterY: sh * cfg.slideTopCenterRatio + imageOffsetY,
      bottomCenterY: sh * cfg.slideBottomCenterRatio + imageOffsetY,
      descY: sh * cfg.slideDescTopRatio,
      topMaxW: sw * cfg.slideTopMaxWRatio * scale,
      topMaxH: sh * cfg.slideTopMaxHRatio * scale,
      bottomMaxW: sw * cfg.slideBottomMaxWRatio * scale,
      bottomMaxH: sh * cfg.slideBottomMaxHRatio * scale,
      singleCenterY: sh * cfg.slideSingleCenterRatio,
      singleMaxW: sw * cfg.slideSingleMaxWRatio * scale,
      singleMaxH: sh * cfg.slideSingleMaxHRatio * scale,
      descFontSize: this._tapFontSize ?? Math.round(sw * cfg.tapFontRatio),
      descWordWrap: sw * 0.88,
    };

    if (cfg.slidePairedRowCenterRatio != null) {
      layout.pairedRowCenterY =
        sh * cfg.slidePairedRowCenterRatio + imageOffsetY;
      layout.pairedRowMaxH = sh * cfg.slidePairedRowMaxHRatio * scale;
      layout.pairedLeftMaxW = sw * cfg.slidePairedLeftMaxWRatio * scale;
      layout.pairedRightMaxW = sw * cfg.slidePairedRightMaxWRatio * scale;
      layout.pairedGap = sw * cfg.slidePairedGapRatio;
    }

    return layout;
  }

  _getFitDimensions(texture, maxW, maxH) {
    const nativeW = texture?.width || maxW;
    const nativeH = texture?.height || maxH;
    if (!nativeW || !nativeH) return { w: maxW, h: maxH };

    const fitScale = Math.min(maxW / nativeW, maxH / nativeH);
    return { w: nativeW * fitScale, h: nativeH * fitScale };
  }

  _fitSpriteInBox(sprite, cx, cy, maxW, maxH, sizeMult = 1) {
    const { w, h } = this._getFitDimensions(sprite.texture, maxW, maxH);
    sprite.width = w * sizeMult;
    sprite.height = h * sizeMult;
    sprite.x = cx;
    sprite.y = cy;
    sprite.visible = true;
  }

  _getSlideCenterOffsetY(slideDef, slideHeight) {
    if (!slideDef || !slideHeight) return 0;
    const ratio = this._layoutIsPortrait
      ? slideDef.portraitCenterOffsetRatio
      : slideDef.landscapeCenterOffsetRatio;
    return slideHeight * (ratio ?? 0);
  }

  _getSlideSizeMult(slideDef) {
    if (!slideDef) return 1;
    if (this._layoutIsPortrait && slideDef.portraitSizeMult) {
      return slideDef.portraitSizeMult;
    }
    if (!this._layoutIsPortrait && slideDef.landscapeSizeMult) {
      return slideDef.landscapeSizeMult;
    }
    return slideDef.sizeMult ?? 1;
  }

  // Scale pot + coins together — sizeMult grows sprites but keeps the layout edge gap fixed.
  _layoutPairedSprites(
    topSprite,
    bottomSprite,
    layout,
    sizeMult,
    centerOffsetY = 0,
  ) {
    const topDims = this._getFitDimensions(
      topSprite.texture,
      layout.topMaxW,
      layout.topMaxH,
    );
    const botDims = this._getFitDimensions(
      bottomSprite.texture,
      layout.bottomMaxW,
      layout.bottomMaxH,
    );

    const topCY = layout.topCenterY;
    const topH = topDims.h * sizeMult;
    const botH = botDims.h * sizeMult;

    topSprite.width = topDims.w * sizeMult;
    topSprite.height = topH;
    topSprite.x = layout.cx;
    topSprite.y = topCY + centerOffsetY;
    topSprite.visible = true;

    // No extra design gap — sprites stack directly touching, relying on the
    // images' own internal padding for visual spacing (same technique as
    // PaytableOverlay's feature slides).
    bottomSprite.width = botDims.w * sizeMult;
    bottomSprite.height = botH;
    bottomSprite.x = layout.cx;
    bottomSprite.y = topCY + topH / 2 + botH / 2 + centerOffsetY;
    bottomSprite.visible = true;
  }

  // Landscape: pot left, coins right — centered as one row.
  _layoutPairedSpritesRow(
    topSprite,
    bottomSprite,
    layout,
    sizeMult,
    centerOffsetY = 0,
  ) {
    const leftDims = this._getFitDimensions(
      topSprite.texture,
      layout.pairedLeftMaxW,
      layout.pairedRowMaxH,
    );
    const rightDims = this._getFitDimensions(
      bottomSprite.texture,
      layout.pairedRightMaxW,
      layout.pairedRowMaxH,
    );

    const leftW = leftDims.w * sizeMult;
    const leftH = leftDims.h * sizeMult;
    const rightW = rightDims.w * sizeMult;
    const rightH = rightDims.h * sizeMult;
    const gap = layout.pairedGap;
    const totalW = leftW + gap + rightW;
    const rowCY = layout.pairedRowCenterY + centerOffsetY;
    const startX = layout.cx - totalW / 2;

    topSprite.width = leftW;
    topSprite.height = leftH;
    topSprite.x = startX + leftW / 2;
    topSprite.y = rowCY;
    topSprite.visible = true;

    bottomSprite.width = rightW;
    bottomSprite.height = rightH;
    bottomSprite.x = startX + leftW + gap + rightW / 2;
    bottomSprite.y = rowCY;
    bottomSprite.visible = true;
  }

  _layoutSlideContents(slideContainer) {
    const layout = this._slideLayout;
    if (!layout) return;

    const children = slideContainer.children;
    const hasBottom = children.length >= 3;
    const topSprite = children[0];
    const bottomSprite = hasBottom ? children[1] : null;
    const descText = hasBottom ? children[2] : children[1];
    if (!topSprite || !descText) return;

    const base = FONT_STYLES.introTapToStart;
    descText.style = new PIXI.TextStyle(
      scaleStyle(base, layout.descFontSize, {
        align: "center",
        wordWrap: true,
        wordWrapWidth: layout.descWordWrap,
      }),
    );
    descText.x = layout.cx;
    descText.y = layout.descY;
    descText.anchor.set(0.5, 0);

    if (!hasBottom) {
      if (bottomSprite) bottomSprite.visible = false;
      const sizeMult = this._getSlideSizeMult(slideContainer.slideDef);
      const centerOffsetY = this._getSlideCenterOffsetY(
        slideContainer.slideDef,
        this._slideHeight,
      );
      this._fitSpriteInBox(
        topSprite,
        layout.cx,
        layout.singleCenterY + centerOffsetY,
        layout.singleMaxW,
        layout.singleMaxH,
        sizeMult,
      );
      return;
    }

    const sizeMult = this._getSlideSizeMult(slideContainer.slideDef);
    const centerOffsetY = this._getSlideCenterOffsetY(
      slideContainer.slideDef,
      this._slideHeight,
    );

    if (!this._layoutIsPortrait && layout.pairedRowCenterY != null) {
      this._layoutPairedSpritesRow(
        topSprite,
        bottomSprite,
        layout,
        sizeMult,
        centerOffsetY,
      );
      return;
    }

    this._layoutPairedSprites(
      topSprite,
      bottomSprite,
      layout,
      sizeMult,
      centerOffsetY,
    );
  }

  _bindDragHandlers() {
    // Bind to the full-screen backdrop so the whole popup is a swipe surface.
    const area = this._blackBg;
    if (!area) return;

    area.on("pointerdown", this._onDragStart.bind(this));
    // globalpointermove fires regardless of what's currently under the pointer,
    // so a swipe that passes over a dot or the sound button keeps tracking.
    area.on("globalpointermove", this._onDragMove.bind(this));
    area.on("pointerup", this._onDragEnd.bind(this));
    area.on("pointerupoutside", this._onDragEnd.bind(this));
    area.on("pointercancel", this._onDragEnd.bind(this));
  }

  _createPaginationDots() {
    this._dotsContainer = new PIXI.Container();
    this._dotsContainer.name = "pagination_dots";
    this._sliderGroup.addChild(this._dotsContainer);

    const activeTex =
      getNewUITexture(DOT_TEX_NAME_ACTIVE) || PIXI.Texture.WHITE;
    const inactiveTex =
      getNewUITexture(DOT_TEX_NAME_INACTIVE) || PIXI.Texture.WHITE;

    this._dotEntries = [];
    for (let i = 0; i < TOTAL_SLIDES; i++) {
      // Real sprite images, not drawn circles — avoids the jagged edges
      // PIXI Graphics produces on curves/border-radius shapes.
      const dot = new PIXI.Sprite(inactiveTex);
      dot.anchor.set(0.5);
      dot.eventMode = "static";
      dot.cursor = "pointer";
      dot.on("pointertap", (e) => {
        if (e?.stopPropagation) e.stopPropagation();
        soundManager.play(SOUND_NAME.button_click);
        this._goToSlide(i);
      });
      this._dotsContainer.addChild(dot);
      this._dotEntries.push({
        gfx: dot,
        index: i,
        activeTex,
        inactiveTex,
      });
    }
  }

  _createTapText() {
    this._tapContainer = new PIXI.Container();
    this._tapContainer.zIndex = 2;

    const text = this.t("tap_to_start").toUpperCase();
    const style = new PIXI.TextStyle(
      scaleStyle(FONT_STYLES.introTapToStart, 24, { align: "center" }),
    );

    this._tapText = new PIXI.Text(text, style);
    this._tapText.anchor.set(0.5);
    this._tapText.resolution = 2;
    this._tapText.eventMode = "static";
    this._tapText.cursor = "pointer";

    this._tapText.on("pointerover", () => {
      if (this._hoverAnim) this._hoverAnim.pause();
      this._hoverAnim = anime({
        targets: this._tapContainer.scale,
        x: 1.08,
        y: 1.08,
        duration: 100,
        easing: "easeOutQuad",
      });
    });

    this._tapText.on("pointerout", () => {
      if (this._hoverAnim) this._hoverAnim.pause();
      this._hoverAnim = anime({
        targets: this._tapContainer.scale,
        x: 1.0,
        y: 1.0,
        duration: 100,
        easing: "easeOutQuad",
      });
    });

    this._tapText.on("pointertap", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (!this._isPressAnimating) {
        this._isPressAnimating = true;
        const origX = this._tapText.scale.x;
        const origY = this._tapText.scale.y;
        if (this._pressAnim) this._pressAnim.pause();
        this._pressAnim = anime({
          targets: this._tapText.scale,
          x: origX * 0.92,
          y: origY * 0.92,
          duration: 65,
          easing: "easeInQuad",
          complete: () => {
            this._pressAnim = anime({
              targets: this._tapText.scale,
              x: origX,
              y: origY,
              duration: 120,
              easing: "easeOutBack",
              complete: () => {
                this._isPressAnimating = false;
              },
            });
          },
        });
      }
      if (this._clickHandler) this._clickHandler(e);
    });

    this._tapContainer.addChild(this._tapText);
    this.container.addChild(this._tapContainer);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SLIDER NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════

  _getRealSlideIndex() {
    if (this._currentSlide < 0) return TOTAL_SLIDES - 1;
    if (this._currentSlide >= TOTAL_SLIDES) return 0;
    return this._currentSlide;
  }

  _getTargetTrackX(slideIndex) {
    return -((slideIndex + 1) * this._slideWidth);
  }

  _setTrackX(x) {
    this._trackPos.x = x;
    if (this._slidesTrack) {
      this._slidesTrack.x = x;
    }
    this._updateSlideOpacities();
  }

  _calculateSlideAlpha(slideGlobalX) {
    const slideWidth = this._slideWidth;
    if (!slideWidth) return 1;
    const distanceFromCenter = Math.abs(slideGlobalX);
    const fadeStartDistance = slideWidth * 0.1;
    const fadeEndDistance = slideWidth * 0.35;
    if (distanceFromCenter <= fadeStartDistance) return 1;
    if (distanceFromCenter >= fadeEndDistance) return 0;
    const t =
      (distanceFromCenter - fadeStartDistance) /
      (fadeEndDistance - fadeStartDistance);
    return 1 - this._fadeEasing(t);
  }

  _updateSlideOpacities() {
    if (!this._slidesTrack || !this._slideWidth) return;
    const trackX = this._trackPos.x;
    // child.x is left edge; measure distance of slide center from mask center
    const maskCenterX = this._slideWidth / 2;

    for (let i = 0; i < this._slidesTrack.children.length; i++) {
      const child = this._slidesTrack.children[i];
      const slideCenterX = trackX + child.x + this._slideWidth / 2;
      child.alpha = this._calculateSlideAlpha(slideCenterX - maskCenterX);
    }
  }

  _cancelTrackAnim() {
    if (this._trackAnim) {
      anime.remove(this._trackPos);
      this._trackAnim = null;
    }
  }

  _syncTrackPosition(isJump) {
    const realIndex = this._getRealSlideIndex();
    if (isJump) {
      this._isJumping = true;
      this._currentSlide = realIndex;
    }
    this._cancelTrackAnim();
    this._setTrackX(this._getTargetTrackX(realIndex));
    if (isJump) {
      this._isJumping = false;
      this._isTransitioning = false;
    }
    this._updatePaginationDots();
  }

  _animateToSlide(nextSlideIndex) {
    if (!this._slidesTrack || !this._slideWidth) return;

    const targetX = this._getTargetTrackX(nextSlideIndex);
    this._cancelTrackAnim();

    if (this._isJumping) {
      this._setTrackX(targetX);
      this._isJumping = false;
      this._isTransitioning = false;
      return;
    }

    const duration = this._isPaginationNav ? 500 : 700;
    this._isPaginationNav = false;

    this._trackAnim = anime({
      targets: this._trackPos,
      x: targetX,
      duration,
      easing: "cubicBezier(.34, 1.30, .64, 1)",
      update: () => {
        if (this._slidesTrack) {
          this._slidesTrack.x = this._trackPos.x;
        }
        this._updateSlideOpacities();
      },
      complete: () => {
        this._trackAnim = null;
        if (this._currentSlide >= TOTAL_SLIDES) {
          this._isJumping = true;
          this._currentSlide = 0;
          this._animateToSlide(0);
        } else if (this._currentSlide < 0) {
          this._isJumping = true;
          this._currentSlide = TOTAL_SLIDES - 1;
          this._animateToSlide(TOTAL_SLIDES - 1);
        } else {
          this._isTransitioning = false;
          this._updatePaginationDots();
        }
      },
    });
  }

  _goNext() {
    if (this._isTransitioning || this._isDragging) return;
    const now = Date.now();
    if (now - this._lastNavTime < NAV_THROTTLE_MS) return;
    this._lastNavTime = now;
    this._resetAutoplayTimer();
    this._isTransitioning = true;
    this._currentSlide += 1;
    this._updatePaginationDots();
    this._animateToSlide(this._currentSlide);
  }

  _goPrev() {
    if (this._isTransitioning || this._isDragging) return;
    const now = Date.now();
    if (now - this._lastNavTime < NAV_THROTTLE_MS) return;
    this._lastNavTime = now;
    this._resetAutoplayTimer();
    this._isTransitioning = true;
    this._currentSlide -= 1;
    this._updatePaginationDots();
    this._animateToSlide(this._currentSlide);
  }

  _goToSlide(index) {
    if (this._isDragging || this._isJumping) return;
    if (index === this._getRealSlideIndex()) return;
    const now = Date.now();
    if (now - this._lastNavTime < NAV_THROTTLE_MS) return;
    this._lastNavTime = now;
    this._resetAutoplayTimer();

    this._cancelTrackAnim();
    const targetX = this._getTargetTrackX(index);
    const direction = index > this._getRealSlideIndex() ? 1 : -1;
    const offsetX = targetX + direction * this._slideWidth * 0.35;
    this._setTrackX(offsetX);

    this._isPaginationNav = true;
    this._isTransitioning = true;
    this._currentSlide = index;
    this._updatePaginationDots();
    this._animateToSlide(index);
  }

  _onDragStart(e) {
    if (this._isTransitioning) return;
    this._isDragging = true;
    this._dragStartX = e.global.x;
    this._dragCurrentX = e.global.x;
    this._dragStartTime = Date.now();
    this._dragStartTrackX = this._trackPos.x;
    this._cancelTrackAnim();
    this._resetAutoplayTimer();
  }

  _onDragMove(e) {
    if (!this._isDragging) return;
    this._dragCurrentX = e.global.x;
    let dragDelta = this._dragCurrentX - this._dragStartX;
    // Clamp travel to one slide width per gesture — the track can only ever
    // reveal the adjacent slide, never scroll past by more than one.
    const maxDelta = this._slideWidth || Infinity;
    if (dragDelta > maxDelta) dragDelta = maxDelta;
    else if (dragDelta < -maxDelta) dragDelta = -maxDelta;
    this._setTrackX(this._dragStartTrackX + dragDelta);
  }

  _onDragEnd(e) {
    if (!this._isDragging) return;

    const dragDistance = this._dragCurrentX - this._dragStartX;
    const dragDuration = Date.now() - this._dragStartTime;
    const velocity = Math.abs(dragDistance / Math.max(dragDuration, 1));

    this._isDragging = false;

    if (Math.abs(dragDistance) <= TAP_MOVE_THRESHOLD) {
      // Clean tap (no real drag) → dismiss popup + fullscreen (same as before).
      // Checked first so a fast tap can't be misread as a flick by velocity.
      if (this._clickHandler) this._clickHandler(e);
    } else if (
      Math.abs(dragDistance) > SWIPE_THRESHOLD ||
      velocity > VELOCITY_THRESHOLD
    ) {
      // Deliberate swipe → navigate (does not close). We're touching the screen,
      // so also request fullscreen — same as a tap, minus the dismiss.
      if (this._fullscreenHandler) this._fullscreenHandler();
      this._resetAutoplayTimer();
      this._isTransitioning = true;
      if (dragDistance > 0) {
        this._currentSlide -= 1;
      } else {
        this._currentSlide += 1;
      }
      this._updatePaginationDots();
      this._animateToSlide(this._currentSlide);
    } else {
      // Indecisive drag (moved, but below swipe threshold) → snap back, no close.
      this._isTransitioning = true;
      this._trackAnim = anime({
        targets: this._trackPos,
        x: this._getTargetTrackX(this._getRealSlideIndex()),
        duration: 300,
        easing: "cubicBezier(.34, 1.30, .64, 1)",
        update: () => {
          if (this._slidesTrack) {
            this._slidesTrack.x = this._trackPos.x;
          }
          this._updateSlideOpacities();
        },
        complete: () => {
          this._trackAnim = null;
          this._isTransitioning = false;
        },
      });
    }
  }

  _updatePaginationDots(bgW = this._layoutBgW, cfg = this._layoutCfg) {
    if (!this._dotEntries.length || !bgW || !cfg) return;

    const activeIndex = this._getRealSlideIndex();
    const dotSize = Math.round(bgW * cfg.dotSizeRatio * 1.1);
    const dotGap = Math.round(bgW * cfg.dotGapRatio * 3.3);
    const totalW = TOTAL_SLIDES * dotSize + (TOTAL_SLIDES - 1) * dotGap;
    const startX = -totalW / 2 + dotSize / 2;

    for (let i = 0; i < this._dotEntries.length; i++) {
      const { gfx, activeTex, inactiveTex } = this._dotEntries[i];
      const isActive = i === activeIndex;

      gfx.texture = isActive ? activeTex : inactiveTex;
      // Explicit width/height (not native texture size) — both circles must
      // render at the exact same size regardless of their source dimensions.
      gfx.width = dotSize;
      gfx.height = dotSize;
      gfx.x = startX + i * (dotSize + dotGap);
      gfx.y = 0;
      // hitArea lives in the sprite's LOCAL (pre-scale) space. Setting
      // width/height above scales the sprite by dotSize/textureSize, so a
      // local dotSize-radius circle collapses to a near-zero on-screen target
      // for large source textures (active vs inactive dots differ). Divide by
      // the sprite scale so every dot gets the same generous on-screen tap
      // zone (~one dot diameter of padding) no matter which texture it uses.
      const sx = Math.abs(gfx.scale.x) || 1;
      const sy = Math.abs(gfx.scale.y) || 1;
      const hx = dotSize / sx;
      const hy = dotSize / sy;
      gfx.hitArea = new PIXI.Rectangle(-hx, -hy, hx * 2, hy * 2);
    }
  }

  // Advances one slide every AUTOPLAY_INTERVAL_MS. Uses the shared PIXI ticker
  // (deltaMS accumulation) instead of setInterval/setTimeout, matching the
  // timing pattern used by every other animated controller in this game.
  _startAutoplay() {
    if (this._autoplayTickerCallback) return;

    this._autoplayElapsed = 0;
    this._autoplayTickerCallback = () => {
      if (this._isDragging || this._isTransitioning) return;

      this._autoplayElapsed += PIXI.Ticker.shared.deltaMS;
      if (this._autoplayElapsed >= AUTOPLAY_INTERVAL_MS) {
        this._autoplayElapsed = 0;
        this._goNext();
      }
    };
    PIXI.Ticker.shared.add(this._autoplayTickerCallback);
  }

  _stopAutoplay() {
    if (!this._autoplayTickerCallback) return;
    PIXI.Ticker.shared.remove(this._autoplayTickerCallback);
    this._autoplayTickerCallback = null;
  }

  // Restarts the 3s countdown — called on any manual navigation so autoplay
  // doesn't immediately fire right after the user just moved the slider.
  _resetAutoplayTimer() {
    this._autoplayElapsed = 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  // Keyboard navigation (ArrowLeft / ArrowRight from the React shell). These
  // reuse the same throttle + transition guards + infinite-loop wrap as swipes.
  nextSlide() {
    this._goNext();
  }

  prevSlide() {
    this._goPrev();
  }

  setClickHandler(handler) {
    this._clickHandler = handler;
  }

  setFullscreenHandler(handler) {
    this._fullscreenHandler = handler;
  }

  playIntro() {
    if (!this._sliderGroup) return;

    if (this._introAnims) this._introAnims.forEach((a) => a.pause());
    this._introAnims = [];

    this._currentSlide = 0;
    this._syncTrackPosition(true);

    this._sliderGroup.alpha = 0;
    this._sliderGroup.scale.set(0, 0);

    this._introAnims.push(
      anime({
        targets: this._sliderGroup,
        alpha: 1,
        duration: 500,
        delay: 25,
        easing: "easeOutCubic",
      }),
    );

    this._introAnims.push(
      anime({
        targets: this._sliderGroup.scale,
        x: 1,
        y: 1,
        duration: 600,
        delay: 25,
        easing: "easeOutCubic",
      }),
    );

    this._startAutoplay();
  }

  updateDimensions(dimensionsContext) {
    this.dimensionsContext = dimensionsContext;
    if (this.initialized) {
      this._updateLayout();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT
  // ═══════════════════════════════════════════════════════════════════════════

  _updateLayout() {
    const { dimensions, device, orientation, uiSymbolSize, isMobileSmall } =
      this.dimensionsContext;
    if (!dimensions) return;

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    const { width, height } = dimensions;

    const layoutCfg = getLayoutConfig(device, isPortrait, isMobileSmall, width);
    const pad = uiSymbolSize * layoutCfg.padMult;
    const sidePad = uiSymbolSize * layoutCfg.sidePadMult;
    const topEdge = pad;
    const botEdge = height - pad;
    const contentH = botEdge - topEdge;
    const contentW = width - 2 * sidePad;
    const contentCY = topEdge + contentH / 2;

    if (this._blackBg) {
      this._blackBg.x = 0;
      this._blackBg.y = 0;
      this._blackBg.width = width;
      this._blackBg.height = height;
    }

    const bgTex = isPortrait ? this._bgPortraitTex : this._bgLandscapeTex;
    let bgW;
    let bgH;
    let bgCX;
    let bgCY;

    if (this._stormBg && bgTex) {
      this._stormBg.texture = bgTex;
      const bgScaleMult = layoutCfg.bgScaleMult;
      const bgNativeW = bgTex.width || (isPortrait ? 388 : 812);
      const bgNativeH = bgTex.height || (isPortrait ? 689 : 406);
      const bgScale =
        Math.min(contentW / bgNativeW, contentH / bgNativeH) * bgScaleMult;
      bgW = bgNativeW * bgScale;
      bgH = bgNativeH * bgScale;
      bgCX = width / 2;
      bgCY = contentCY;

      this._stormBg.x = bgCX;
      this._stormBg.y = bgCY;
      this._stormBg.width = bgW;
      this._stormBg.height = bgH;
    } else {
      bgW = width;
      bgH = height;
      bgCX = width / 2;
      bgCY = contentCY;
    }

    const cfg = isPortrait ? PORTRAIT_CFG : LANDSCAPE_CFG;
    this._layoutIsPortrait = isPortrait;
    this._layoutBgW = bgW;
    this._layoutCfg = cfg;
    this._layoutBgCX = bgCX;
    this._layoutBgCY = bgCY;

    if (this._logo && this._logoTex) {
      const logoW = bgW * cfg.logoWRatio;
      const logoH = logoW / (this._logoTex.width / this._logoTex.height);
      this._logo.x = bgCX;
      this._logo.y = bgCY + bgH * cfg.logoYRatio;
      this._logo.width = logoW;
      this._logo.height = logoH;
    }

    if (this._maxWinText) {
      const maxWinBudgetW = bgW * 0.85;
      const maxWinTextStr = this._maxWinText.text;
      const base = FONT_STYLES.winText;
      let fontSize = Math.round(bgW * cfg.maxWinFontRatio);
      const tempStyle = new PIXI.TextStyle(scaleStyle(base, fontSize));
      const metrics = PIXI.TextMetrics.measureText(maxWinTextStr, tempStyle);
      if (metrics.width > maxWinBudgetW) {
        fontSize = Math.round(fontSize * (maxWinBudgetW / metrics.width));
      }
      this._maxWinText.style = new PIXI.TextStyle(
        scaleStyle(base, fontSize, { align: "center" }),
      );
      this._maxWinText.x = width / 2;
      this._maxWinText.y = bgCY + bgH * cfg.maxWinYRatio;
    }

    this._updateTapTextStyle(bgW, bgH, bgCY, cfg, width);
    this._updateSlider(isPortrait, bgW, bgH, bgCX, bgCY, cfg);
  }

  _computeLandscapeContentBottomY(bgW, bgH, bgCY, cfg) {
    const cardW = bgW * (cfg.cardWRatio ?? 0.29);
    const tex = PIXI.Assets.get("intro_feature_mystery_pot");
    const nativeW = tex?.width ?? 542;
    const nativeH = tex?.height ?? 746;
    const cardH = cardW / (nativeW / nativeH);
    const centerY = bgCY + bgH * (cfg.contentCenterYRatio ?? 0.08);
    return centerY + cardH / 2;
  }

  _resolveTapTextY(bgH, bgCY, cfg) {
    if (cfg.tapYRatio != null) {
      return bgCY + bgH * cfg.tapYRatio;
    }

    const contentBottomY = this._computeLandscapeContentBottomY(
      this._layoutBgW,
      bgH,
      bgCY,
      cfg,
    );
    this._contentBottomY = contentBottomY;
    const tapOffsetY = bgH * (cfg.tapYOffsetRatio ?? 0);
    return contentBottomY + bgH * cfg.tapGapRatio + tapOffsetY;
  }

  _updateTapTextStyle(bgW, bgH, bgCY, cfg, screenWidth) {
    if (!this._tapContainer || !this._tapText) return;

    const tapFontSize = Math.round(bgW * cfg.tapFontRatio);
    const base = FONT_STYLES.introTapToStart;
    const tapTextStr = this._tapText.text;
    const tempStyle = new PIXI.TextStyle(scaleStyle(base, tapFontSize));
    const metrics = PIXI.TextMetrics.measureText(tapTextStr, tempStyle);
    const finalFontSize =
      metrics.width > screenWidth * 0.9
        ? Math.round(tapFontSize * ((screenWidth * 0.9) / metrics.width))
        : tapFontSize;

    this._tapText.style = new PIXI.TextStyle(
      scaleStyle(base, finalFontSize, { align: "center" }),
    );
    this._tapFontSize = finalFontSize;
    this._tapTextY = this._resolveTapTextY(bgH, bgCY, cfg);
  }

  _updateSlider(isPortrait, bgW, bgH, bgCX, bgCY, cfg) {
    if (!this._slidesMaskContainer || !this._slidesTrack) return;

    const prevSlideWidth = this._slideWidth;
    const prevRealIndex = this._getRealSlideIndex();

    const maxWinBottom = this._maxWinText
      ? this._maxWinText.y + this._maxWinText.height / 2
      : bgCY + bgH * cfg.maxWinYRatio;
    const tapTextH =
      this._tapText?.height ?? Math.round(bgW * cfg.tapFontRatio);
    const dotSize = Math.round(bgW * cfg.dotSizeRatio * 1.1);

    this._tapTextY = this._resolveTapTextY(bgH, bgCY, cfg);
    this._dotsCenterY =
      this._tapTextY - tapTextH / 2 - bgH * cfg.dotsGapRatio - dotSize / 2;

    const slideAreaTop = maxWinBottom + bgH * cfg.slideTopGapRatio;
    const slideBottomPad = bgH * (cfg.slideAreaBottomPadRatio ?? 0.015);
    const slideAreaBottomFromDots =
      this._dotsCenterY - dotSize / 2 - slideBottomPad;
    const slideAreaBottom =
      cfg.slideAreaBottomRatio != null
        ? bgCY + bgH * cfg.slideAreaBottomRatio
        : slideAreaBottomFromDots;
    const slideAreaHeight = Math.max(40, slideAreaBottom - slideAreaTop);
    const slideAreaWidth = bgW * 0.94;
    const slideAreaX = bgCX - slideAreaWidth / 2;

    this._slideWidth = slideAreaWidth;
    this._slideHeight = slideAreaHeight;
    this._slideLayout = this._computeSlideLayout(
      slideAreaWidth,
      slideAreaHeight,
      cfg,
    );

    // Mask clips on Y only: it keeps its tight vertical bounds (so slides never
    // bleed into the max-win text or dots) but overscans far on X so the sides
    // are never hard-cut — the per-slide opacity fade handles left/right
    // transitions naturally as slides enter and leave.
    const maskOverscanX =
      this.dimensionsContext?.dimensions?.width || slideAreaWidth * 3;
    this._slidesMaskGfx.clear();
    this._slidesMaskGfx.beginFill(0xffffff);
    this._slidesMaskGfx.drawRect(
      -maskOverscanX,
      0,
      slideAreaWidth + maskOverscanX * 2,
      slideAreaHeight,
    );
    this._slidesMaskGfx.endFill();

    this._slidesMaskContainer.x = slideAreaX;
    this._slidesMaskContainer.y = slideAreaTop;

    // Track slides (incl. clones) are built once in _createSlider — on resize we
    // only reposition/refit them. Rebuilding here would re-create the clone
    // containers every resize without destroying the old ones (a leak).
    this._layoutTrackSlidePositions();

    this._currentSlide = prevRealIndex;
    if (prevSlideWidth !== this._slideWidth) {
      this._cancelTrackAnim();
    }
    this._syncTrackPosition(true);

    if (this._dotsContainer) {
      this._dotsContainer.x = bgCX;
      this._dotsContainer.y = this._dotsCenterY;
      this._updatePaginationDots(bgW, cfg);
    }

    if (this._sliderGroup) {
      this._sliderGroup.pivot.set(bgCX, bgCY);
      this._sliderGroup.x = bgCX;
      this._sliderGroup.y = bgCY;
    }

    if (this._tapContainer && this._tapText) {
      this._tapContainer.x = bgCX;
      this._tapContainer.y = this._tapTextY;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════════════════

  cleanup() {
    this._stopAutoplay();
    if (this._hoverAnim) {
      this._hoverAnim.pause();
      this._hoverAnim = null;
    }
    if (this._pressAnim) {
      this._pressAnim.pause();
      this._pressAnim = null;
    }
    if (this._introAnims) {
      this._introAnims.forEach((a) => a.pause());
      this._introAnims = null;
    }
    this._cancelTrackAnim();

    if (this._slidesMaskContainer) {
      this._slidesMaskContainer.removeAllListeners();
    }
    for (const dot of this._dotEntries) {
      dot.gfx.removeAllListeners();
    }

    if (this._blackBg) {
      this._blackBg.destroy();
      this._blackBg = null;
    }
    if (this._stormBg) {
      this._stormBg.destroy();
      this._stormBg = null;
    }
    if (this._logo) {
      this._logo.destroy();
      this._logo = null;
    }
    if (this._maxWinText) {
      this._maxWinText.destroy();
      this._maxWinText = null;
    }

    if (this._sliderGroup) {
      this._sliderGroup.destroy({ children: true });
      this._sliderGroup = null;
    }
    this._slidesMaskContainer = null;
    this._slidesMaskGfx = null;
    this._slidesTrack = null;
    this._realSlides = [];
    this._dotsContainer = null;
    this._dotEntries = [];

    if (this._tapContainer) {
      if (this._tapText) {
        this._tapText.removeAllListeners();
      }
      this._tapContainer.destroy({ children: true });
      this._tapContainer = null;
      this._tapText = null;
    }

    this._clickHandler = null;
    this._fullscreenHandler = null;
    this.initialized = false;
  }
}

export default IntroPopupComposedController;
